import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Sparkles } from "lucide-react";

export function Shell({ title, nav = [], children, portalLabel }) {
  const { user, logout } = useAuth();
  const nav_ = useNavigate();
  const loc = useLocation();

  return (
    <div className="min-h-screen relative flex text-white overflow-hidden" data-testid="portal-shell">
      <div className="aurora" />
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 flex-col glass border-r border-white/10 relative z-10">
        <div className="px-6 py-6 border-b border-white/10">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <div className="font-display font-extrabold text-lg leading-none">SyncLine</div>
              <div className="text-[10px] uppercase tracking-widest text-mute mt-1">{portalLabel}</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1">
          {nav.map((n) => {
            const active = loc.pathname === n.to || (n.match && loc.pathname.startsWith(n.match));
            return (
              <Link key={n.to} to={n.to}
                data-testid={n.testId}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${active ? "bg-white/10 text-white" : "text-mute hover:bg-white/5 hover:text-white"}`}>
                {n.icon && <n.icon className="w-4 h-4" />}
                <span>{n.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-mute mb-2 truncate" data-testid="shell-user-email">{user?.email}</div>
          <button
            onClick={() => { logout(); nav_("/"); }}
            data-testid="shell-logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm bg-white/5 hover:bg-white/10 border border-white/10"
          >
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </aside>

      <main className="flex-1 relative z-10 min-w-0">
        <header className="glass border-b border-white/10 px-6 py-4 flex items-center justify-between sticky top-0 z-20">
          <div>
            <h1 className="font-display font-extrabold text-2xl leading-none" data-testid="page-title">{title}</h1>
            <div className="text-xs text-mute mt-1 lg:hidden">SyncLine · {portalLabel}</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-xs text-mute hidden sm:block" data-testid="shell-role">{user?.role?.toUpperCase()}</div>
            <button onClick={() => { logout(); nav_("/"); }} className="lg:hidden bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-md text-xs" data-testid="mobile-logout-btn">
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </header>
        <div className="p-6 lg:p-8">{children}</div>
      </main>
    </div>
  );
}

export function KpiCard({ label, value, accent = "primary", testId }) {
  const accents = {
    primary: "from-indigo-500/30 to-fuchsia-500/10",
    green: "from-emerald-500/25 to-emerald-500/5",
    amber: "from-amber-500/25 to-amber-500/5",
    red: "from-rose-500/25 to-rose-500/5",
    blue: "from-sky-500/25 to-sky-500/5",
  };
  return (
    <div className="glass rounded-2xl p-5 relative overflow-hidden rise-in" data-testid={testId}>
      <div className={`absolute -top-16 -right-16 w-40 h-40 rounded-full bg-gradient-to-br ${accents[accent]} blur-2xl`} />
      <div className="text-[11px] uppercase tracking-widest text-mute font-medium">{label}</div>
      <div className="font-display font-extrabold text-3xl mt-2" data-testid={testId ? `${testId}-value` : undefined}>{value}</div>
    </div>
  );
}

export function Section({ title, action, children, testId }) {
  return (
    <section className="glass rounded-2xl p-6 rise-in-2" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-bold text-lg">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export function StatusPill({ status }) {
  const map = {
    live: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    draft: "bg-slate-500/15 text-slate-300 border-slate-500/30",
    paused: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    closed: "bg-white/5 text-mute border-white/10",
    complete: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    terminate: "bg-rose-500/15 text-rose-300 border-rose-500/30",
    overquota: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    quality_term: "bg-rose-500/10 text-rose-200 border-rose-500/25",
    security_term: "bg-rose-500/10 text-rose-200 border-rose-500/25",
    started: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  };
  const cls = map[status] || "bg-white/5 text-mute border-white/10";
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border ${cls}`} data-testid={`status-${status}`}>{status}</span>;
}
